import React from 'react';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Box,
  Grid,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper
} from '@mui/material';
import { deleteRequest, getRequest } from 'Includes/api/apicall';
import { DEL_URL, GET_URL } from 'Includes/urls';
import { Link, useNavigate } from 'react-router-dom';
import { AddCircleOutlineOutlined, FilterList } from "@mui/icons-material";
import Swal from 'sweetalert2';

const BILLING_CHOICES = {
  'monthly': 'Monthly',
  'termly': 'Termly',
  'one_time': 'One-time',
};

function ServicePlanList(props) {
  const navigate = useNavigate();
  // 🔴 ORIGINAL STATE (UNCHANGED)
  const [rows, setRows] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);

  const [serviceList, setServiceList] = React.useState([]);
  const [selectedService, setSelectedService] = React.useState('');
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });

  const columns = [
    { field: 'id', headerName: 'ID', width: 70, hide: true },

    {
      field: 'service_name',
      headerName: 'Service',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.row.service?.name || params.row.service_name || '-'
    },

    {
      field: 'name',
      headerName: 'Plan Name',
      flex: 1,
      minWidth: 180,
    },

    {
      field: 'billing_name',
      headerName: 'Billing Name',
      flex: 1,
      minWidth: 180,
    },

    {
      field: 'billing_cycle',
      headerName: 'Billing Cycle',
      width: 140,
      renderCell: (params) => (
        <Chip
          label={BILLING_CHOICES[params.value] || params.value}
          size="small"
          color="primary"
          variant="outlined"
        />
      )
    },

    {
      field: 'service_type',
      headerName: 'Service Type',
      width: 140,
    },

    {
      field: 'base_price',
      headerName: 'Base Price',
      width: 140,
      renderCell: (params) =>
        `₹${parseFloat(params.value || 0).toLocaleString('en-IN')}`
    },

    {
      field: 'default_is_gst_applicable',
      headerName: 'GST',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Yes' : 'No'}
          size="small"
          color={params.value ? 'success' : 'default'}
        />
      )
    },

    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      getActions: (params) => [
        <GridActionsCellItem label="Edit" onClick={() => handleEdit(params.row)} showInMenu />,
        <GridActionsCellItem label="Delete" onClick={() => handleDelete(params.row.id)} showInMenu />,
      ]
    }
  ];

  const handleEdit = (row) => {
    navigate(`/billing/service-plan?id=${row.id}`);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.value) {
        const url = `${DEL_URL.plans?.api}${id}/`;
        setLoading(true);
        deleteRequest(url, {}, props).then(response => {
          setLoading(false);
          if (response?.status === 200) {
            getServicePlanList();
            Swal.fire('Deleted!', 'Service Plan deleted.', 'success');
          }
        }).catch(() => setLoading(false));
      }
    });
  };

  const handleServiceFilterChange = (e) => {
    const serviceId = e.target.value;
    setSelectedService(serviceId);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const clearFilter = () => {
    setSelectedService('');
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  React.useEffect(() => {
    getServiceList();
  }, []);

  React.useEffect(() => {
    getServicePlanList();
  }, [paginationModel.page, paginationModel.pageSize, selectedService]);

  const getServiceList = () => {
    getRequest(GET_URL.services?.api, {}, props)
      .then(res => {
        if (res?.status === 200) {
          setServiceList(res.data.data || []);
        }
      });
  };

  const getServicePlanList = () => {
    setLoading(true);
    const params = {
      limit: paginationModel.pageSize,
      pageno: paginationModel.page + 1,
      ...(selectedService ? { service: selectedService } : {}),
    };
    getRequest(GET_URL.plans.api, params, props)
      .then(res => {
        if (res?.status === 200) {
          const plans = res.data.data || [];
          setRows(plans);
          setRowCount(res.data?.count || plans.length);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  return (
    <Box sx={{ p: 3 }}>

      {/* 🔥 HEADER */}
      <Grid container alignItems="center">
        <Grid item xs={12} md={6}>
          <Typography variant="h5" fontWeight={600}>
            Service Plans
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage service plans and pricing
          </Typography>
        </Grid>

        <Grid item xs={12} md={6} textAlign="right">
          <Button
            variant="contained"
            component={Link}
            to={'/billing/service-plan'}
            startIcon={<AddCircleOutlineOutlined />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              px: 2,
              py: 1,
              boxShadow: "0 4px 14px rgba(0,0,0,0.1)"
            }}
          >
            Add Service Plan
          </Button>
        </Grid>
      </Grid>

      {/* 🔥 FILTER CARD */}
      <Paper sx={{ mt: 3, p: 2, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
        <Grid container spacing={2} alignItems="center">

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>
                <FilterList sx={{ mr: 1 }} />
                Filter by Service
              </InputLabel>
              <Select
                value={selectedService}
                onChange={handleServiceFilterChange}
                label="Filter by Service"
              >
                <MenuItem value="">
                  <em>All Services</em>
                </MenuItem>
                {serviceList.map((service) => (
                  <MenuItem key={service.id} value={service.id}>
                    {service.name} ({service.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {selectedService && (
            <Grid item>
              <Button onClick={clearFilter}>Clear</Button>
            </Grid>
          )}

          <Grid item>
            <Typography variant="body2" color="text.secondary">
              Showing {rows.length} of {rowCount}
            </Typography>
          </Grid>

        </Grid>
      </Paper>

      {/* 🔥 TABLE CARD */}
      <Paper sx={{ mt: 3, p: 2, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          rowCount={rowCount}
          autoHeight
          rowHeight={44}
          disableSelectionOnClick
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f7fb",
              fontWeight: 600,
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#fafafa",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #f0f0f0",
            }
          }}
        />
      </Paper>

    </Box>
  );
}

export default ServicePlanList;